import React, { Component } from "react";

import AlgoliaContainer from "core-web/containers/AlgoliaContainer";
import Events from "core-web/libs/Events";
import { ProductEvents } from "core-web/libs/Events/constants";
import PropTypes from "prop-types";
import getQueryParams from "core-web/utils/getQueryParams";
import { inServer } from "core-web/constants";
import { injectModel } from "core-web/state";
import transformArticle from "core-web/utils/dataTransformers/transformArticle";
import { withRouter } from "react-router-dom";

class SearchContainer extends Component {
    /**
     * @return array [pagination, pageSize]
     */
    static productsPaginationReset = (props) => {
        const firstPageSize = props.firstPageSize || props.pageSize;
        const { location, pagination, pageSize } = props;
        const query = getQueryParams(location);
        const newPagination = query.page ? query.page : pagination || 1;
        const newPageSize = newPagination === 1 ? firstPageSize : pageSize;

        return [newPagination, newPageSize];
    };

    // Function to change query param to new page (called in rendered component)
    pagination = {
        nextUrl: () => {
            const { location } = this.props;
            const query = inServer ? getQueryParams(location) : getQueryParams(window.location);

            if (!query.page) {
                query.page = 1;
            }

            query.page++;

            // todo: when nothing more to load return empty string

            return `${location.pathname}?${Object.entries(query)
                .map((q) => q.join("="))
                .join("&")}`;
        },

        // todo create prevUrl()
    };

    static propTypes = {
        application: PropTypes.object.isRequired, //this is redux store application
        history: PropTypes.object.isRequired,
        indices: PropTypes.shape({
            articles: PropTypes.shape({
                firstPageSize: PropTypes.number,
                page: PropTypes.number,
                pageSize: PropTypes.number,
            }),
        }),
        location: PropTypes.object.isRequired,
        match: PropTypes.object.isRequired,
        productProps: PropTypes.shape({
            distinct: PropTypes.bool,
            filters: PropTypes.shape({
                // this filter is set and never changed for one time.
                default: PropTypes.array,
                // This filter is the active filter that changes
                selected: PropTypes.array,
                // This filter is the active dynamic filter that changes
                dynamic: PropTypes.array,
            }),
            firstPageSize: PropTypes.number,
            index: PropTypes.string,
            pageSize: PropTypes.number,
            pagination: PropTypes.number,
            responseCallback: PropTypes.func,
        }),
        render: PropTypes.func.isRequired,
        renderProps: PropTypes.object,
        ruleContexts: PropTypes.array,
        search: PropTypes.string,
        view: PropTypes.string,
        withPagination: PropTypes.bool,
    };

    static defaultProps = {
        indices: {
            articles: { firstPageSize: 12, page: 1, pageSize: 12 },
        },
        productProps: {
            distinct: true,
            filters: {
                default: [],
                selected: [],
                dynamic: [],
            },
            firstPageSize: null,
            index: "products",
            pageSize: 24,
            pagination: 1,
            responseCallback: null,
        },
        renderProps: {},
        ruleContexts: [],
        search: "",
        view: null,
        withPagination: true,
    };

    constructor(props) {
        super(props);
        const [pagination, pageSize] = SearchContainer.productsPaginationReset(props.productProps);

        this.state = {
            articles: this.setArticlesState(),
            products: {
                pageSize,
                pagination,
            },
        };
    }

    static getDerivedStateFromProps(props, state) {
        const { location, productProps } = props;
        const firstPageSize = productProps.firstPageSize || productProps.pageSize;
        const query = inServer ? getQueryParams(location) : getQueryParams(window.location);

        // Update products pagination and pageSize state from url queries
        if (
            parseInt(query.page, 10) !== state.products.pagination &&
            (firstPageSize === state.products.pageSize || productProps.pageSize === state.products.pageSize)
        ) {
            state.products.pagination = query.page ? parseInt(query.page, 10) : 1;
            state.products.pageSize = state.pagination > 1 ? productProps.pageSize : firstPageSize;
        }

        // Update articles page state from url queries
        if (parseInt(query.page, 10) !== state.articles.page) {
            state.articles.page = query.page ? parseInt(query.page, 10) : 1;
        }

        return state;
    }

    shouldComponentUpdate(nextProps, nextState) {
        let shouldReturnTrue = false;
        if (this.shouldUpdateDueToProductChanges(nextProps)) {
            shouldReturnTrue = true;
        }

        if (this.shouldUpdateDueToArticleChanges(nextProps, nextState)) {
            shouldReturnTrue = true;
        }

        return shouldReturnTrue;
    }

    componentDidUpdate(prevProps) {
        this.handleProductComponentDidUpdate(prevProps);
        this.handleArticleComponentDidUpdate(prevProps);
    }

    shouldUpdateDueToProductChanges(nextProps) {
        const { application, location, history, match, ...props } = this.props;
        const {
            application: nextApplication,
            location: nextLocation,
            history: nextHistory,
            match: nextMatch,
            ...newNextProps
        } = nextProps;

        if (this.props.view && nextProps.view && this.props.view !== "products" && nextProps.view !== "products") {
            return false;
        }

        if (
            (location.search !== nextLocation.search && location.pathname === nextLocation.pathname) ||
            JSON.stringify(props) !== JSON.stringify(newNextProps)
        ) {
            return true;
        }

        if (application.locale !== nextApplication.locale) {
            return true;
        }

        return false;
    }

    shouldUpdateDueToArticleChanges(nextProps, nextState) {
        const { location, search } = this.props;

        if (this.props.view && nextProps.view && this.props.view !== "articles" && nextProps.view !== "articles") {
            return false;
        }

        if (nextState.articles.page !== this.state.articles.page) {
            return true;
        }

        if (nextProps.search !== search && location.pathname === nextProps.location.pathname) {
            this.setArticlesUrl();
            return true;
        }

        if (JSON.stringify(nextProps.location.search) !== JSON.stringify(location.search)) {
            return true;
        }

        return false;
    }

    handleArticleComponentDidUpdate(prevProps) {
        const { search } = this.props;
        if (prevProps.search !== search) {
            this.setState({ articles: this.setArticlesState() });
        }
    }

    handleProductComponentDidUpdate(prevProps) {
        const {
            productProps: { filters },
            search,
        } = this.props;
        if (JSON.stringify(prevProps.productProps.filters) !== JSON.stringify(filters) || prevProps.search !== search) {
            const [pagination, pageSize] = SearchContainer.productsPaginationReset(this.props.productProps);

            //todo: reset container function?
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                products: {
                    pagination,
                    pageSize,
                },
            });
        }
    }

    setArticlesState() {
        const queries = getQueryParams(inServer ? this.props.location : window.location);
        const page = queries.page || this.props.indices.articles.page;

        return {
            page,
        };
    }

    setArticlesUrl() {
        const { history, location } = this.props;

        const params = getQueryParams(window.location);

        delete params.page;

        const url = `?${Object.entries(params)
            .map((q) => q.join("="))
            .join("&")}`;

        history.replace(`${location.pathname}${url}`);
    }

    // Build up pagination object for articles grid
    getArticlesPagination = (currentIndex, posts) => {
        const {
            indices: {
                articles: { firstPageSize },
            },
            location,
        } = this.props;
        const query = inServer ? getQueryParams(location) : getQueryParams(window.location);
        delete query.page;

        const offset = posts.offset;
        const length = posts.length;
        const hitsCount = posts.nbHits;
        const pageSize = length;
        const pagination = firstPageSize > pageSize ? Math.floor(offset / length) : Math.ceil(offset / length);
        const pageCount =
            firstPageSize > 0 ? Math.ceil((hitsCount - firstPageSize) / pageSize) + 1 : Math.ceil(hitsCount / pageSize);

        const createUrl = (page) => {
            return `${location.pathname}?${Object.entries({ ...query, page })
                .map((q) => q.join("="))
                .join("&")}`;
        };
        return {
            pages: [...new Array(pageCount)].map((page, index) => ({
                name: index + 1,
                current: currentIndex === index,
                url: createUrl(index + 1),
            })),
            previousUrl: currentIndex !== 0 && createUrl(Math.max(1, currentIndex)),
            nextUrl: currentIndex + 1 !== pageCount && createUrl(currentIndex + 2),
            hasMore: posts.hasMore,
            hits: posts.hits.length,
            hitsCount: posts.nbHits,
            page: pagination,
            pageCount,
            pageSize,
            offset,
        };
    };

    productParameters = (
        filters,
        facets = ["*"],
        ruleContexts,
        offset,
        length,
        distinct,
        facetingAfterDistinct = true,
    ) => ({
        facets,
        offset,
        length,
        ruleContexts,
        distinct,
        facetingAfterDistinct,
        filters: filters.filter((i) => i.length !== 0),
    });

    // Build up parameters for included indices to search Algolia
    getParameters = () => {
        const { indices, productProps, application, ruleContexts, withPagination } = this.props;
        const {
            articles: { page: articlesPageState },
            products: { pagination: productsPaginationState, pageSize: productsPageSizeState },
        } = this.state;

        let { search } = this.props;

        const allParameters = [];

        const locale = application.locale.toLowerCase();

        const localeIndices = {
            articles: `posts_${locale || process.env.REACT_APP_DEFAULT_LOCALE}`,
            categories: `posts_${locale || process.env.REACT_APP_DEFAULT_LOCALE}`,
            categoriesBrands: `posts_${locale || process.env.REACT_APP_DEFAULT_LOCALE}`,
            products: `products_${locale || process.env.REACT_APP_DEFAULT_LOCALE}`,
            suggestions: `suggestions_${locale || process.env.REACT_APP_DEFAULT_LOCALE}`,
        };

        const productsPage =
            !withPagination || isNaN(productsPaginationState) || productsPaginationState <= 1
                ? 0
                : productsPaginationState - 1;
        const productsOffset =
            productsPage === 0 ? 0 : productProps.firstPageSize + productsPageSizeState * (productsPage - 1);
        const productsLength = productsPage === 1 ? productProps.firstPageSize : productsPageSizeState;

        // todo: only get default once per changed filter default
        // default query
        try {
            search = decodeURI(search);
        } catch (e) {
            // console.error(e, 'Get params');
        }

        // Push product selections as paramaters
        allParameters.push({
            indexName: `${productProps.index}_${locale}`,
            query: search,
            params: this.productParameters(
                productProps.filters.default,
                ["*"],
                ruleContexts,
                productsOffset,
                productsLength,
                productProps.distinct,
                productProps.distinct,
            ),
        });

        // Push user-selected product filters as parameters
        if (search !== "" || productProps.filters.selected.length > 0) {
            const selected = productProps.filters.default.concat(productProps.filters.selected);

            allParameters.push({
                indexName: `${productProps.index}_${locale}`,
                query: search,
                params: this.productParameters(
                    selected,
                    ["*"],
                    ruleContexts,
                    productsOffset,
                    productsLength,
                    productProps.distinct,
                    productProps.distinct,
                ),
            });
        }

        // Push user-selected dynamic product filters as parameters
        (productProps.filters.dynamic || []).forEach((filterParameter) => {
            if (search !== "" || filterParameter.filters.length > 0) {
                const selected = productProps.filters.default.concat(filterParameter.filters);

                allParameters.push({
                    indexName: `${productProps.index}_${locale}`,
                    query: search,
                    params: this.productParameters(
                        selected,
                        filterParameter.facets,
                        ruleContexts,
                        productsOffset,
                        productsLength,
                        productProps.distinct,
                        productProps.distinct,
                    ),
                });
            }
        });

        // Push rest of indices as parameters
        Object.keys(indices).forEach((index) => {
            const indexParams = indices[index];
            let filters = [];
            let restrictSearchableAttributes = [];
            let length = indexParams.pageSize;
            let offset = 0;
            switch (index) {
                case "articles":
                    filters = [["post_type:article"], ["template:default"]];
                    offset =
                        !withPagination || articlesPageState === 1
                            ? 0
                            : indices.articles.firstPageSize + indices.articles.pageSize * (articlesPageState - 2);
                    length =
                        !withPagination || articlesPageState === 1
                            ? indices.articles.firstPageSize
                            : indices.articles.pageSize;
                    break;
                case "categories":
                    filters = [["post_type:storm_category"]];
                    // restrictSearchableAttributes = ["title"];
                    break;
                case "categoriesBrands":
                    filters = [["post_type:storm_category", "post_type:storm_brand"]];
                    // restrictSearchableAttributes = ["title"];
                    break;
                default:
                    break;
            }
            allParameters.push({
                query: search,
                indexName: localeIndices[index],
                params: {
                    filters,
                    offset,
                    length,
                    restrictSearchableAttributes,
                },
            });
        });

        return allParameters;
    };

    // Format response data for products
    productsResponseCallback = (response) => {
        const {
            productProps: { responseCallback, pageSize, filters, firstPageSize },
        } = this.props;
        const { pagination } = this.state.products;

        const defaultProducts = response[0];
        const products = response[1] || {};

        // take products and use defaultProducts as fallback
        // this is used when there is no props.filters.selected
        const get = (d, p, k) => (p[k] !== undefined ? p[k] : d[k]);

        const offset = get(defaultProducts, products, "offset");
        const length = get(defaultProducts, products, "length");
        const newPageSize = get(defaultProducts, products, "hitsPerPage");
        const nbHits = get(defaultProducts, products, "nbHits");
        const hitsCount = nbHits;
        const newPagination = newPageSize > pageSize ? newPageSize / pageSize : pagination;
        const page = Math.ceil(offset / length);
        const pageCount =
            firstPageSize > 0 ? Math.ceil((hitsCount - firstPageSize) / pageSize) + 1 : Math.ceil(hitsCount / pageSize);

        Events.trigger(ProductEvents.IMPRESSIONS, {
            products: get(defaultProducts, products, "hits"),
            offset: page > 0 ? page * newPageSize : 0,
        });

        let facets = defaultProducts?.facets;
        let facetsStats = defaultProducts?.facets_stats;

        if (response.length > 2 && filters.dynamic?.length) {
            response.slice(2).forEach((res) => {
                facets = get(defaultProducts, products, "facets");
                facetsStats = get(defaultProducts, products, "facets_stats");

                const { facets: responseFacets, facets_stats: responseFacetsStats } = res;

                Object.keys(responseFacets).forEach((key) => {
                    facets[key] = responseFacets[key];
                    if (responseFacetsStats && facetsStats[key] && responseFacetsStats[key]) {
                        facetsStats[key] = responseFacetsStats[key];
                    }
                });
            });
        } else if (filters.dynamic?.length > 0) {
            const key = filters.dynamic[0].facets;
            const foundFacet = facets[key];
            const foundFacetState = facetsStats[key];

            facets = get(defaultProducts, products, "facets");
            facetsStats = get(defaultProducts, products, "facets_stats");

            facets[key] = foundFacet;
            facetsStats[key] = foundFacetState;
        }

        const productsData = {
            filters: facets,
            filter_stats: facetsStats,
            products: get(defaultProducts, products, "hits"),
            productsCount: get(defaultProducts, products, "hits").length,
            page,
            offset,
            pageSize: length,
            pageCount,
            hitsCount,
            hasMore: get(defaultProducts, products, "hasMore"),
            pagination: this.pagination,
            queryId: defaultProducts.queryID,
        };

        this.setState({
            products: {
                pageSize: length,
                pagination: newPagination,
            },
        });

        // If passed down, call responseCallback from parent to apply it's formating of data
        return responseCallback ? responseCallback(productsData) : productsData;
    };

    // Format response data for post indices
    postsResponseCallback = (responses) => {
        const articles = responses[0];
        const categoriesBrands = responses[1];
        const suggestions = responses[2];
        const filters = {};

        return {
            articles: {
                hits: articles.hits.map((article) => transformArticle(article)),
                hitsCount: articles.nbHits,
                filters,
                pagination: this.getArticlesPagination(articles?.page, articles),
                query: articles.query,
            },
            categoriesBrands: {
                hits: categoriesBrands.hits,
            },
            suggestions: {
                hits: suggestions.hits,
            },
        };
    };

    responseCallback = (response) => {
        if (!response) {
            return null;
        }

        const productResponses = response.filter((singleResponse) => singleResponse.index.includes("products"));
        const postsResponses = response.filter((singleResponse) => !singleResponse.index.includes("products"));
        const productsData = this.productsResponseCallback(productResponses);
        const postsData = this.postsResponseCallback(postsResponses);

        return { productsData, postsData };
    };

    render() {
        const { render, renderProps } = this.props;
        return (
            <AlgoliaContainer
                parameters={this.getParameters()}
                responseCallback={this.responseCallback}
                render={({ application, ...rest }) => render({ ...rest, renderProps })}
            />
        );
    }
}

export default withRouter(injectModel("application")(SearchContainer));
